/**
 * The evaluation outbox — writes that outlive the network (SE-6983).
 *
 * Connectivity at lower-league grounds is bad, and a scout at the ground is
 * the one person who cannot retry later from a desk. Today a failed write
 * survives only as an in-memory delta on the panel that made it: it retries on
 * the scout's next interaction (SE-7549), which is honest, but it dies with a
 * reload, a crash, or the app being killed while backgrounded.
 *
 * This store is what makes it survive that. Every write that cannot reach the
 * server is parked here, on the device, and drained when the network comes
 * back.
 *
 * ## Merged per target, never stacked
 *
 * Entries are keyed by `(owner, matchReportId, playerId)` and MERGED on
 * enqueue. That is a correctness property, not compaction. The write contract
 * is "an omitted field is left unchanged; an explicit null clears it", so two
 * queued deltas for one player are not two independent writes — replaying an
 * older delta after a newer one would copy stale fields over fresh ones, which
 * is the exact failure `useAutosaveFields` already serialises its in-flight
 * writes to avoid. Merging keeps last-write-wins per FIELD, which is what the
 * scout typed.
 *
 * `undefined` and `null` stay distinct through the merge: `null` is a real
 * value meaning "clear this field" and must survive; a field nobody touched is
 * simply absent.
 *
 * ## Every entry belongs to the user AND organisation that queued it (SE-8075)
 *
 * `UpsertPlayerEvaluationInput` carries no identity, so the server attributes
 * a replayed write to whoever's session is on the request. SE-7720 answered
 * that by clearing the queue on every sign-out — which kept the next user from
 * draining it, and destroyed the scout's unsent work without a word.
 *
 * So the queue is scoped instead of cleared. Each entry records its
 * {@link OutboxOwner} on the device — never on the wire — and only a session
 * with that exact owner may drain, peek at, or merge into it. Other owners'
 * entries stay parked and untouched until their owner is back. That covers
 * every way a session ends, including token expiry, which runs with no user
 * and no UI, because it depends on no transition running any code.
 *
 * The organisation is part of the owner, not only the user. The server
 * resolves the report under the caller's CURRENT organisation and authoring
 * scout (`requireOwnedReportEntity`), so the same scout's write replayed while
 * acting in another organisation is refused — and the drain discards what the
 * server refuses. Scoping by user alone would turn an organisation switch into
 * lost work.
 *
 * ## Why the queue is not the Apollo cache
 *
 * Apollo's own retry link would re-send the operation, not the merged intent,
 * and it holds nothing across a process restart. The unit that has to survive
 * here is the scout's edit, and the smallest honest representation of that is
 * the delta plus the ids it belongs to.
 * @module features/match-report-capture/stores/evaluationOutbox
 */

import AsyncStorage from "@react-native-async-storage/async-storage";
import { makeVar } from "@apollo/client";

import type { AuthUserFragment } from "@/generated/graphql";

/**
 * AsyncStorage key for the persisted queue. Namespaced to the feature so it
 * never collides with the `@scouting_*` or `@player_kanban_*` keys.
 */
export const EVALUATION_OUTBOX_STORAGE_KEY = "@match_report_evaluation_outbox";

/**
 * Upper bound on parked writes, PER OWNER.
 *
 * A full 22-player report is 22 entries, so this holds several reports' worth
 * of unsynced work. It is a guard against unbounded growth on a device that
 * never regains signal, not a working limit — and when it bites, the OLDEST
 * entry is dropped, because the newest edits are the ones the scout still
 * remembers making.
 *
 * Per owner, because a shared device holds several people's parked work: a
 * global cap would let one scout's edits evict another's, which is the
 * cross-user data loss the owner scoping exists to rule out.
 */
export const EVALUATION_OUTBOX_MAX = 200;

/**
 * Who a parked write belongs to. Local only — it never reaches the server.
 */
export interface OutboxOwner {
  /** The user whose session queued the write. */
  readonly userId: string;
  /** The organisation that user was acting in when it was queued. */
  readonly organizationId: string;
}

/** One parked write: the fields, the two ids they belong to, and the owner. */
export interface QueuedEvaluation {
  /** Whose session queued this, and therefore the only session that may drain it. */
  readonly owner: OutboxOwner;
  /** The report the evaluation belongs to. */
  readonly matchReportId: string;
  /** The player the evaluation is about. */
  readonly playerId: string;
  /**
   * The accumulated delta. Never a snapshot — see the module docs on why a
   * snapshot would wipe fields the scout had already set.
   */
  readonly fields: Readonly<Record<string, unknown>>;
  /** When this entry was first parked, for ordering and for the toast copy. */
  readonly queuedAt: number;
}

/** Which evaluation, for whom. */
type EvaluationTarget = Pick<
  QueuedEvaluation,
  "owner" | "matchReportId" | "playerId"
>;

/** What hydration loaded, and what it had to leave behind. */
export interface OutboxHydration {
  /** The queue after hydration, every owner's entries included. */
  readonly entries: readonly QueuedEvaluation[];
  /**
   * Entries parked by a build that did not yet record an owner, and which
   * were therefore dropped rather than loaded. See {@link hydrateEvaluationOutbox}.
   */
  readonly unattributed: number;
}

/**
 * The parked writes, oldest first, for every owner on the device.
 *
 * Read reactively so the banner and the per-evaluation save state can render
 * from it; mutated only through the functions below. Anything that acts on
 * behalf of a session reads it through {@link ownedEvaluations}.
 */
export const evaluationOutboxVar = makeVar<readonly QueuedEvaluation[]>([]);

/**
 * The owner a session parks under, or null when the session has no resolved
 * identity to park under.
 *
 * Null is not a default to fill in. A write with no provable owner is one no
 * session may ever drain, so a caller holding null must not park at all.
 * @param user - The current user, as `useCurrentUser` returns it.
 * @returns The owner, or null without both a user id and an organisation.
 */
export const outboxOwnerFor = (
  user: Pick<AuthUserFragment, "id" | "organizationId"> | null | undefined
): OutboxOwner | null => {
  if (!user?.id || !user.organizationId) return null;

  return { userId: user.id, organizationId: user.organizationId };
};

/**
 * Whether two owners are the same user in the same organisation.
 * @param a - One owner.
 * @param b - Another owner.
 * @returns True when both halves match.
 */
export const isSameOwner = (
  a: OutboxOwner | null,
  b: OutboxOwner | null
): boolean =>
  a !== null &&
  b !== null &&
  a.userId === b.userId &&
  a.organizationId === b.organizationId;

/**
 * The entries one owner may act on, oldest first.
 * @param entries - The whole queue.
 * @param owner - The session's owner; null yields nothing.
 * @returns That owner's entries only.
 */
export const ownedEvaluations = (
  entries: readonly QueuedEvaluation[],
  owner: OutboxOwner | null
): readonly QueuedEvaluation[] =>
  entries.filter(entry => isSameOwner(entry.owner, owner));

/**
 * Whether two entries describe the same evaluation for the same owner.
 * @param a - One entry.
 * @param b - Another entry.
 * @returns True when the owner and both ids match.
 */
const isSameTarget = (a: EvaluationTarget, b: EvaluationTarget): boolean =>
  isSameOwner(a.owner, b.owner) &&
  a.matchReportId === b.matchReportId &&
  a.playerId === b.playerId;

/**
 * Persists the queue. Fire-and-forget by design.
 *
 * A storage failure must never block or fail the edit that is already in the
 * in-memory queue: the scout's work is safe for this session either way, and
 * surfacing a storage error as a save error would report a worse state than
 * the one they are in.
 * @param entries - The queue to persist.
 */
const persist = (entries: readonly QueuedEvaluation[]): void => {
  void AsyncStorage.setItem(
    EVALUATION_OUTBOX_STORAGE_KEY,
    JSON.stringify(entries)
  ).catch(() => undefined);
};

/**
 * Drops one owner's oldest entries beyond {@link EVALUATION_OUTBOX_MAX},
 * leaving every other owner's entries exactly where they were.
 * @param entries - The queue, oldest first.
 * @param owner - Whose entries to cap.
 * @returns The capped queue.
 */
const capOwner = (
  entries: readonly QueuedEvaluation[],
  owner: OutboxOwner
): readonly QueuedEvaluation[] => {
  const owned = ownedEvaluations(entries, owner);
  const overflow = owned.length - EVALUATION_OUTBOX_MAX;
  if (overflow <= 0) return entries;

  const evicted = new Set(owned.slice(0, overflow));

  return entries.filter(entry => !evicted.has(entry));
};

/**
 * Parks a write, merging it into any entry already queued for that evaluation
 * by the same owner.
 * @param entry - The write to park, without its timestamp.
 * @param now - Current epoch milliseconds; injected so tests need no clock.
 * @returns The queue after the enqueue.
 */
export const enqueueEvaluation = (
  entry: Omit<QueuedEvaluation, "queuedAt">,
  now: number
): readonly QueuedEvaluation[] => {
  const current = evaluationOutboxVar();
  const existing = current.find(candidate => isSameTarget(candidate, entry));

  const merged: QueuedEvaluation = {
    owner: entry.owner,
    matchReportId: entry.matchReportId,
    playerId: entry.playerId,
    // Newer fields win; untouched fields from the earlier delta survive.
    fields: { ...existing?.fields, ...entry.fields },
    // Keeps the ORIGINAL parking time. The scout has been waiting since then,
    // and resetting it on every keystroke would make "queued since" useless.
    queuedAt: existing?.queuedAt ?? now,
  };

  const withoutTarget = current.filter(
    candidate => !isSameTarget(candidate, entry)
  );
  const next = capOwner([...withoutTarget, merged], entry.owner);

  evaluationOutboxVar(next);
  persist(next);

  return next;
};

/**
 * Removes one evaluation from the queue, after it has been written.
 * @param target - Which evaluation to drop, and for whom.
 * @returns The queue after the removal.
 */
export const dequeueEvaluation = (
  target: EvaluationTarget
): readonly QueuedEvaluation[] => {
  const next = evaluationOutboxVar().filter(
    candidate => !isSameTarget(candidate, target)
  );

  evaluationOutboxVar(next);
  persist(next);

  return next;
};

/**
 * The entry parked for an evaluation by this owner, or null when none is.
 *
 * Exists so a live write can CARRY the parked fields rather than destroy them:
 * see `usePlayerEvaluations`' `onSave`, where dequeuing on success used to drop
 * a note that had never been sent. Owner-scoped, so a live write never carries
 * fields another user parked.
 * @param target - Which evaluation to look up, and for whom.
 * @returns The queued entry, or null.
 */
export const peekEvaluation = (
  target: EvaluationTarget
): QueuedEvaluation | null =>
  evaluationOutboxVar().find(candidate => isSameTarget(candidate, target)) ??
  null;

/**
 * Whether two field maps carry the same values.
 *
 * Shallow, because a delta's values are scalars by construction — the write
 * contract is per-field, and `undefined` never survives into a queued entry.
 * @param a - One field map.
 * @param b - Another field map.
 * @returns True when both hold the same keys with the same values.
 */
const haveSameFields = (
  a: Readonly<Record<string, unknown>>,
  b: Readonly<Record<string, unknown>>
): boolean => {
  const keys = Object.keys(a);

  return (
    keys.length === Object.keys(b).length &&
    keys.every(key => a[key] === b[key])
  );
};

/**
 * Removes an entry only if it still holds exactly what was sent.
 *
 * A drain spans many round trips, and `enqueueEvaluation` MERGES — so a write
 * that fails on transport while the drain is mid-flight folds newer fields into
 * the very entry the drain is about to remove. Removing by target alone would
 * then delete fields that were never sent and will never be retried, which is
 * the failure the queue exists to prevent, reached from inside the queue's own
 * drain.
 *
 * When the entry has changed, it is LEFT PARKED and replays on the next pass.
 * That re-sends the fields already accepted, which costs one idempotent write
 * and is the safe direction: the upsert is last-write-wins per field, and the
 * values being re-sent are the ones just stored.
 * @param entry - The entry as it was sent.
 * @returns True when the entry was removed; false when it had changed.
 */
export const dequeueEvaluationIfUnchanged = (
  entry: QueuedEvaluation
): boolean => {
  const current = peekEvaluation(entry);
  if (current === null) return true;
  if (!haveSameFields(current.fields, entry.fields)) return false;

  dequeueEvaluation(entry);

  return true;
};

/**
 * Whether a parsed value is a usable owner.
 * @param value - A parsed candidate.
 * @returns True when both halves are non-empty strings.
 */
const isOutboxOwner = (value: unknown): value is OutboxOwner => {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<OutboxOwner>;

  return (
    typeof candidate.userId === "string" &&
    candidate.userId !== "" &&
    typeof candidate.organizationId === "string" &&
    candidate.organizationId !== ""
  );
};

/**
 * Whether a parsed value carries a replayable write, owner aside.
 * @param value - A parsed candidate.
 * @returns True when the entry carries both ids and a fields object.
 */
const hasReplayableWrite = (
  value: unknown
): value is Omit<QueuedEvaluation, "owner"> => {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<QueuedEvaluation>;

  return (
    typeof candidate.matchReportId === "string" &&
    candidate.matchReportId !== "" &&
    typeof candidate.playerId === "string" &&
    candidate.playerId !== "" &&
    typeof candidate.queuedAt === "number" &&
    candidate.fields !== null &&
    typeof candidate.fields === "object"
  );
};

/**
 * Whether a parsed value is a usable queue entry.
 *
 * Hydration reads whatever is on the device, which may have been written by an
 * older build with a different shape. An entry that cannot be replayed is
 * dropped rather than repaired: a half-understood delta sent to the server is
 * worse than a lost one, because it writes fields nobody chose.
 * @param value - A parsed candidate.
 * @returns True when the entry carries an owner, both ids and a fields object.
 */
const isQueuedEvaluation = (value: unknown): value is QueuedEvaluation =>
  hasReplayableWrite(value) &&
  isOutboxOwner((value as Partial<QueuedEvaluation>).owner);

/**
 * Whether a parsed value is a write parked before entries recorded an owner.
 * @param value - A parsed candidate.
 * @returns True for a replayable write with no owner at all.
 */
const isUnattributedEntry = (value: unknown): boolean =>
  hasReplayableWrite(value) &&
  (value as Partial<QueuedEvaluation>).owner === undefined;

/**
 * Loads the persisted queue into memory. Call once, at session start.
 *
 * Needs no session: it loads EVERY owner's entries, because who may drain an
 * entry is decided when draining, not when loading.
 *
 * A read failure or unparseable blob leaves the queue empty rather than
 * throwing: the app must open even when the device's storage is unreadable,
 * and an empty outbox degrades to today's behaviour rather than to a crash.
 *
 * ## Entries from before the owner was recorded are dropped, and counted
 *
 * A build before SE-8075 parked entries with no owner. Nothing on the device
 * can say whose they are — the session on the device now is not evidence, since
 * builds before SE-7720 never cleared the queue between users — and replaying
 * one under the wrong session is precisely the defect the scoping removes. So
 * they are never replayed. They are dropped for the same reason
 * {@link isQueuedEvaluation} drops other unreplayable shapes, and COUNTED, so
 * the drain can tell the scout that work was not kept instead of losing it in
 * silence.
 * @returns The hydrated queue, and how many unattributable entries were dropped.
 */
export const hydrateEvaluationOutbox = async (): Promise<OutboxHydration> => {
  const unchanged = (): OutboxHydration => ({
    entries: evaluationOutboxVar(),
    unattributed: 0,
  });

  try {
    const raw = await AsyncStorage.getItem(EVALUATION_OUTBOX_STORAGE_KEY);
    if (raw === null) return unchanged();

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return unchanged();

    const stored = parsed.filter(isQueuedEvaluation);
    const unattributed = parsed.filter(isUnattributedEntry).length;

    // MERGED, never replaced — and the in-memory side wins per target.
    //
    // The read is asynchronous, so a write can be parked while it is in
    // flight. Replacing the queue would drop that write, and the next
    // `persist` would then delete it from storage too: the store losing the
    // exact thing it exists to keep.
    //
    // The same race can already have cost the STORED entries, which is why
    // this persists rather than only updating memory. `enqueueEvaluation`
    // writes `evaluationOutboxVar()` plus its entry, so one landing before
    // hydration writes a queue of one — overwriting everything on disk. Union
    // and re-persist puts them back.
    const inMemory = evaluationOutboxVar();
    const restored = stored.filter(
      candidate => !inMemory.some(live => isSameTarget(live, candidate))
    );
    const next = [...restored, ...inMemory];

    evaluationOutboxVar(next);
    // Compared against what was READ, not what was kept, so a dropped entry is
    // removed from storage too — otherwise it would be counted again at every
    // launch and reported as newly lost each time.
    if (restored.length !== parsed.length || inMemory.length > 0) {
      persist(next);
    }

    return { entries: next, unattributed };
  } catch {
    return unchanged();
  }
};
